import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useForm, useField } from 'react-final-form-hooks';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import uid from 'uid';

import { useTheme } from '../../ThemeContext';

import {
  dataSourceAddInit,
  dataSourceAdd,
  dataSourceEdit,
} from '../../actions/DataSource/DataSourceActions';

import Modal from '../Modal';
import Select from '../Select';

import {
  Form,
  Wrapper,
  Header,
  Title,
  Content,
  Footer,
  Button,
  Box,
  Row,
  Item,
  ItemInner,
  SubtitleHolder,
  Subtitle,
  TypeTitle,
  InputboxHolder,
  Inputbox,
  Input,
  Textbox,
  Text,
  Link,
  Icon,
  Tagsbox,
  Tag,
  TagName,
  TagIcon,
  InputButton,
} from './DataSourceAdd.style';

const DataSourceAddInner = ({ data, handleClose = () => {} }) => {
  const intl = useIntl();
  const theme = useTheme();
  const { profile } = useSelector((state) => state.firebase);
  const isProfileCompany = profile.company && profile.company.id ? true : false;
  const [photoCategoryFieldsCount, setPhotoCategoryFieldsCount] = useState(
    (data && data.photoCategories && data.photoCategories.length) || 1
  );
  const [objectsCategoryFieldsCount, setObjectsCategoryFieldsCount] = useState(
    (data && data.objectsCategories && data.objectsCategories.length) || 1
  );
  const [photoCategories, setPhotoCategories] = useState(
    (data &&
      data.photoCategories &&
      data.photoCategories.reduce((currentState, category) => {
        if (!currentState[category.id])
          currentState[category.id] = {
            name: category.name,
            tags: category.tags,
          };
        return currentState;
      }, {})) ||
      {}
  );

  const [objectsCategories, setObjectsCategories] = useState(
    (data &&
      data.objectsCategories &&
      data.objectsCategories.reduce((currentState, category) => {
        if (!currentState[category.id])
          currentState[category.id] = {
            name: category.name,
            tags: category.tags,
          };
        return currentState;
      }, {})) ||
      {}
  );

  const { isLoading, isSuccess, isError, error } = useSelector(
    (state) => state.dataSourceAddStore
  );
  const dispatch = useDispatch();

  const validate = (values) => {
    const errors = {};
    if (!values.name)
      errors.name = intl.formatMessage({
        id: 'FORM.VALIDATION.THIS_FIELD_IS_REQUIRED',
      });
    if (!values.type)
      errors.type = intl.formatMessage({
        id: 'FORM.VALIDATION.THIS_FIELD_IS_REQUIRED',
      });
    return errors;
  };

  const onSubmit = (values) => {
    if (isProfileCompany) {
      const newDataSourceData = {
        id: data && data.id,
        name: values.name,
        photo_categories: Object.entries(values.photoCategories).reduce(
          (currentState, [id, category]) => {
            if (!currentState[id])
              currentState[id] = {
                name: category.name,
                tags: category.tags,
              };
            return currentState;
          },
          {}
        ),
        objects_categories: Object.entries(values.objectsCategories).reduce(
          (currentState, [id, category]) => {
            if (!currentState[id])
              currentState[id] = {
                name: category.name,
                tags: category.tags,
              };
            return currentState;
          },
          {}
        ),
      };

      if (data && data.id) dispatch(dataSourceEdit(newDataSourceData));
      else dispatch(dataSourceAdd(newDataSourceData));
    }
  };

  const { form, handleSubmit, pristine, submitting, invalid } = useForm({
    onSubmit,
    validate,
    initialValues: {
      name: data && data.name,
      type: data
        ? data.photoCategories &&
          data.photoCategories.length > 0 &&
          data.objectsCategories &&
          data.objectsCategories.length > 0
          ? 'both'
          : data.photoCategories && data.photoCategories.length > 0
          ? 'photo'
          : data.objectsCategories && data.objectsCategories.length > 0
          ? 'objects'
          : undefined
        : undefined,
    },
  });
  const nameField = useField('name', form);
  const typeField = useField('type', form);
  useField('photoCategories', form);
  useField('objectsCategories', form);

  useEffect(() => {
    dispatch(dataSourceAddInit());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    form.change('photoCategories', photoCategories);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoCategories]);

  useEffect(() => {
    form.change('objectsCategories', objectsCategories);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectsCategories]);

  const renderCategoryField = (index, type) => {
    const handleAddCategoryField = () => {
      if (type === 'photo_categories')
        setPhotoCategoryFieldsCount(photoCategoryFieldsCount + 1);
      else if (type === 'objects_categories')
        setObjectsCategoryFieldsCount(objectsCategoryFieldsCount + 1);
    };

    const handleCategoryName = (id = uid(20), value) => {
      if (type === 'photo_categories')
        setPhotoCategories(
          Object.assign({}, photoCategories, {
            [id]: {
              ...photoCategories[id],
              name: value,
            },
          })
        );
      else if (type === 'objects_categories')
        setObjectsCategories(
          Object.assign({}, objectsCategories, {
            [id]: {
              ...objectsCategories[id],
              name: value,
            },
          })
        );
    };

    const handleCreateCategoryTags = (categoryId = uid(20), value) => {
      if (value) {
        const tagId = uid(20);
        if (type === 'photo_categories')
          setPhotoCategories(
            Object.assign({}, photoCategories, {
              [categoryId]: {
                ...photoCategories[categoryId],
                tags: {
                  ...(photoCategories[categoryId] &&
                    photoCategories[categoryId].tags),
                  [tagId]: value,
                },
              },
            })
          );
        else if (type === 'objects_categories')
          setObjectsCategories(
            Object.assign({}, objectsCategories, {
              [categoryId]: {
                ...objectsCategories[categoryId],
                tags: {
                  ...(objectsCategories[categoryId] &&
                    objectsCategories[categoryId].tags),
                  [tagId]: value,
                },
              },
            })
          );
      }
    };

    const categories =
      type === 'photo_categories'
        ? photoCategories
        : type === 'objects_categories'
        ? objectsCategories
        : undefined;

    return (
      <ItemInner>
        {index === 0 && type && typeField.input.value === 'both' ? (
          <TypeTitle theme={theme.dataSourceAdd.typeTitle}>
            {intl.formatMessage({
              id: `ADD_DATA_SOURCE.${
                type === 'photo_categories'
                  ? 'PHOTO'
                  : type === 'objects_categories'
                  ? 'OBJECTS'
                  : ''
              }`,
            })}
            :
          </TypeTitle>
        ) : (
          ''
        )}
        <SubtitleHolder>
          {index === 0 ? (
            <Subtitle theme={theme.dataSourceAdd.subtitle}>
              {intl.formatMessage({
                id: 'ADD_DATA_SOURCE.DEFINE_CATEGORIES_AND_TAGS',
              })}
            </Subtitle>
          ) : (
            ''
          )}
          {type ? (
            index === 0 ? (
              <Link theme={theme.link} onClick={() => handleAddCategoryField()}>
                <Icon className='material-icons'>add</Icon>
                Add another category
              </Link>
            ) : (
              ''
            )
          ) : (
            ''
          )}
        </SubtitleHolder>
        <InputboxHolder>
          <Inputbox>
            <Input
              theme={theme.input.default}
              name={type[index].name}
              defaultValue={
                categories &&
                Object.values(categories)[index] &&
                Object.values(categories)[index].name
              }
              placeholder={intl.formatMessage({
                id: 'ADD_DATA_SOURCE.INPUT.CATEGORY',
              })}
              onChange={(event) =>
                handleCategoryName(
                  categories && Object.keys(categories)[index],
                  event.currentTarget.value
                )
              }
            />
          </Inputbox>
          <Inputbox>
            <Input
              theme={theme.input.default}
              placeholder={intl.formatMessage({
                id: 'ADD_DATA_SOURCE.INPUT.ADD_TAG',
              })}
              onBlur={(event) => {
                handleCreateCategoryTags(
                  categories && Object.keys(categories)[index],
                  event.currentTarget.value
                );
                event.currentTarget.value = '';
              }}
            />
            <InputButton
              theme={theme.button.normal}
              className='material-icons'
              noMask
              widthAuto
            >
              add
            </InputButton>
          </Inputbox>
        </InputboxHolder>
        {categories &&
          Object.entries(categories)[index] &&
          renderCategoryTags(Object.entries(categories)[index], type)}
      </ItemInner>
    );
  };

  const renderCategoryTags = (category, type) => {
    const handleChangeCategoryTags = (categoryId, tagId, value) => {
      if (value) {
        if (type === 'photo_categories')
          setPhotoCategories(
            Object.assign({}, photoCategories, {
              [categoryId]: {
                ...photoCategories[categoryId],
                tags: {
                  ...(photoCategories[categoryId] &&
                    photoCategories[categoryId].tags),
                  [tagId]: value,
                },
              },
            })
          );
        else if (type === 'objects_categories')
          setObjectsCategories(
            Object.assign({}, objectsCategories, {
              [categoryId]: {
                ...objectsCategories[categoryId],
                tags: {
                  ...(objectsCategories[categoryId] &&
                    objectsCategories[categoryId].tags),
                  [tagId]: value,
                },
              },
            })
          );
      } else {
        handleRemoveCategoryTags(categoryId, tagId);
      }
    };

    const handleRemoveCategoryTags = (categoryId, tagId) => {
      if (type === 'photo_categories')
        setPhotoCategories(
          Object.assign({}, photoCategories, {
            [categoryId]: {
              ...photoCategories[categoryId],
              tags: {
                ...(photoCategories[categoryId] &&
                  Object.fromEntries(
                    Object.entries(photoCategories[categoryId].tags).filter(
                      (tag) => tag[0] !== tagId
                    )
                  )),
              },
            },
          })
        );
      else if (type === 'objects_categories')
        setObjectsCategories(
          Object.assign({}, objectsCategories, {
            [categoryId]: {
              ...objectsCategories[categoryId],
              tags: {
                ...(objectsCategories[categoryId] &&
                  Object.fromEntries(
                    Object.entries(objectsCategories[categoryId].tags).filter(
                      (tag) => tag[0] !== tagId
                    )
                  )),
              },
            },
          })
        );
    };

    return (
      <Tagsbox>
        {category &&
          category[1] &&
          category[1].tags &&
          Object.entries(category[1].tags).map((tag) => (
            <Tag theme={theme.dataSourceAdd.tag} key={tag[0]}>
              <TagName
                contentEditable={true}
                onBlur={(event) =>
                  handleChangeCategoryTags(
                    category[0],
                    tag[0],
                    event.currentTarget.innerHTML
                  )
                }
              >
                {tag[1]}
              </TagName>
              <TagIcon
                className='material-icons'
                onClick={() => handleRemoveCategoryTags(category[0], tag[0])}
              >
                close
              </TagIcon>
            </Tag>
          ))}
      </Tagsbox>
    );
  };

  const renderSuccessPanel = () => {
    return (
      <Wrapper theme={theme.dataSourceAdd.wrapper}>
        <Header theme={theme.dataSourceAdd.header}>
          <Title theme={theme.dataSourceAdd.title}>
            {intl.formatMessage({
              id: 'ADD_DATA_SOURCE.SUCCESS',
            })}
          </Title>
        </Header>
        <Content>
          <Box>
            <Textbox center>
              <Text theme={theme.dataSourceAdd.text.default}>
                {intl.formatMessage({
                  id: `ADD_DATA_SOURCE.TEXT.${
                    data && data.id
                      ? 'THE_DATA_SOURCE_HAS_BEEN_SUCCESSFULLY_UPDATED'
                      : 'THE_DATA_SOURCE_HAS_BEEN_SUCCESSFULLY_CREATED'
                  }`,
                })}
              </Text>
            </Textbox>
          </Box>
        </Content>
        <Footer>
          <Button
            type='button'
            theme={theme.button.default}
            onClick={() => handleClose()}
          >
            {intl.formatMessage({ id: 'ADD_DATA_SOURCE.OK' })}
          </Button>
        </Footer>
      </Wrapper>
    );
  };

  const renderFormPanel = () => {
    return (
      <Form onSubmit={handleSubmit}>
        <Wrapper theme={theme.dataSourceAdd.wrapper}>
          <Header theme={theme.dataSourceAdd.header}>
            <Title theme={theme.dataSourceAdd.title}>
              {intl.formatMessage({
                id: `ADD_DATA_SOURCE.${
                  data && data.id ? 'EDIT_DATA_SOURCE' : 'ADD_A_NEW_DATA_SOURCE'
                }`,
              })}
            </Title>
          </Header>
          <Content>
            <Box>
              <Row theme={theme.dataSourceAdd.row}>
                <Item>
                  <Subtitle theme={theme.dataSourceAdd.subtitle}>
                    {intl.formatMessage({
                      id: 'ADD_DATA_SOURCE.DEFINE_A_DATA_SOURCE_NAME',
                    })}
                  </Subtitle>
                  <Inputbox>
                    <Input
                      theme={theme.input.default}
                      {...nameField.input}
                      placeholder={intl.formatMessage({
                        id: 'ADD_DATA_SOURCE.INPUT.NAME',
                      })}
                      invalid={nameField.meta.touched && nameField.meta.error}
                    />
                  </Inputbox>
                </Item>
                <Item>
                  <Subtitle theme={theme.dataSourceAdd.subtitle}>
                    {intl.formatMessage({
                      id: 'ADD_DATA_SOURCE.DEFINE_A_DATA_SOURCE_TYPE',
                    })}
                  </Subtitle>
                  <Inputbox>
                    <Select
                      value={typeField.input.value}
                      options={[
                        {
                          label: intl.formatMessage({
                            id: 'ADD_DATA_SOURCE.OPTION.LABEL_PHOTO',
                          }),
                          value: 'photo',
                        },
                        {
                          label: intl.formatMessage({
                            id: 'ADD_DATA_SOURCE.OPTION.LABEL_OBJECTS',
                          }),
                          value: 'objects',
                        },
                        {
                          label: intl.formatMessage({
                            id:
                              'ADD_DATA_SOURCE.OPTION.LABEL_PHOTO_AND_OBJECTS',
                          }),
                          value: 'both',
                        },
                      ]}
                      placeholderMessage={'ADD_DATA_SOURCE.INPUT.TYPE'}
                      onChange={(value) => {
                        typeField.input.onChange(value);
                      }}
                      onBlur={() => {
                        typeField.input.onBlur();
                      }}
                      invalid={typeField.meta.touched && typeField.meta.error}
                    />
                  </Inputbox>
                </Item>
              </Row>
              <Row theme={theme.dataSourceAdd.row} grow>
                {typeField.input.value ? (
                  <>
                    {typeField.input.value === 'photo' ||
                    typeField.input.value === 'both' ? (
                      <Item
                        single={
                          typeField.input.value === 'photo' ? true : undefined
                        }
                      >
                        {[...Array(photoCategoryFieldsCount)].map((_, key) => (
                          <Fragment key={key}>
                            {renderCategoryField(key, 'photo_categories')}
                          </Fragment>
                        ))}
                      </Item>
                    ) : (
                      ''
                    )}

                    {typeField.input.value === 'objects' ||
                    typeField.input.value === 'both' ? (
                      <Item
                        single={
                          typeField.input.value === 'objects' ? true : undefined
                        }
                      >
                        {[...Array(objectsCategoryFieldsCount)].map(
                          (_, key) => (
                            <Fragment key={key}>
                              {renderCategoryField(key, 'objects_categories')}
                            </Fragment>
                          )
                        )}
                      </Item>
                    ) : (
                      ''
                    )}
                  </>
                ) : (
                  <Textbox center>
                    <Text theme={theme.dataSourceAdd.text.default}>
                      {intl.formatMessage({
                        id:
                          'ADD_DATA_SOURCE.TEXT.YOU_NEED_TO_DEFINE_A_DETECTION_TYPE_TO_SEE_CATEGORIES_AND_TAGS',
                      })}
                    </Text>
                  </Textbox>
                )}
              </Row>
            </Box>
          </Content>
          {isError ? (
            <Textbox>
              <Text theme={theme.dataSourceAdd.text.error}>
                {intl.formatMessage({
                  id: `ADD_DATA_SOURCE.TEXT.ERROR.${error}`,
                  defaultMessage: error,
                })}
              </Text>
            </Textbox>
          ) : (
            ''
          )}
          <Footer>
            <Button
              type='button'
              theme={theme.button.alt}
              disabled={isLoading}
              onClick={() => handleClose()}
            >
              {intl.formatMessage({ id: 'ADD_DATA_SOURCE.CANCEL' })}
            </Button>
            <Button
              theme={theme.button.default}
              disabled={submitting || pristine || invalid || isLoading}
            >
              {isLoading
                ? intl.formatMessage({ id: 'ADD_DATA_SOURCE.PLEASE_WAIT' })
                : intl.formatMessage({
                    id: `ADD_DATA_SOURCE.${data && data.id ? 'EDIT' : 'ADD'}`,
                  })}
            </Button>
          </Footer>
        </Wrapper>
      </Form>
    );
  };

  return <>{isSuccess ? renderSuccessPanel() : renderFormPanel()}</>;
};

const DataSourceAdd = ({ data, handleClose = () => {} }) => {
  return (
    <Modal handleClose={() => handleClose()}>
      <DataSourceAddInner data={data} handleClose={() => handleClose()} />
    </Modal>
  );
};

DataSourceAddInner.propTypes = {
  data: PropTypes.object,
  handleClose: PropTypes.func,
};

DataSourceAdd.propTypes = {
  data: PropTypes.object,
  handleClose: PropTypes.func,
};

export default DataSourceAdd;
